/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/auth/RequireAuth.tsx
 * Release: v2.0.0 - Protezione route autenticate Coronatek V2
 * Data: Dicembre 2024
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';

interface RequireAuthProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  allowedGroups?: string[];
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children, allowedRoles, allowedGroups }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-slate-300">Caricamento sessione...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Ci si ricorda DOVE voleva andare: prima si finiva sempre in dashboard e la pagina
    // richiesta andava ritrovata a mano (segnalato aprendo /accesso-fred, 2026-08-03).
    // Viaggia nell'URL e non in `state` perché il login fa window.location.replace,
    // che ricarica la pagina e butta via lo stato del router.
    const destinazione = `${location.pathname}${location.search}`;
    const next = destinazione && destinazione !== '/dashboard'
      ? `?next=${encodeURIComponent(destinazione)}`
      : '';
    return <Navigate to={`/login${next}`} replace />;
  }

  // Controlla permessi per ruoli o gruppi
  const hasRoleAccess = !allowedRoles || allowedRoles.length === 0 || allowedRoles.includes(user?.role || '');
  const hasGroupAccess = !allowedGroups || allowedGroups.length === 0 || allowedGroups.includes(user?.group_id || '');
  
  if (!hasRoleAccess && !hasGroupAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default RequireAuth; 